import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Play, BookOpen, Puzzle, Calculator, Award } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import QuebonLogo from '@/assets/quebon-logo.svg'

// 배너 데이터
const bannerMessages = [
  { text: '구구단 안 외우면 어떻게?', highlight: '구구단 말고 깨구단 하자!' },
  { text: '천재의 두뇌 만들기 프로젝트!', highlight: '초등 필수 학습서 출간!' },
  { text: '수학을 언어처럼 쉽게!', highlight: '이미지로 재밌게 배우자!' },
]

// 카테고리 필터
const categories = [
  { id: 'calculation', name: '연산', icon: Calculator },
  { id: 'sequence', name: '수열', icon: BookOpen },
  { id: 'geometry', name: '도형', icon: Puzzle },
  { id: 'comparison', name: '비교', icon: Award },
  { id: 'average', name: '평균', icon: Calculator },
]

// 학습 탭
const learningTabs = ['커리큘럼', '영상', '문제', '게임', '워크북']

export function LandingPage() {
  const { isAuthenticated } = useAuthStore()
  const [bannerIndex, setBannerIndex] = useState(0)
  const [activeCategory, setActiveCategory] = useState('calculation')
  const [activeTab, setActiveTab] = useState('커리큘럼')

  // 배너 자동 슬라이드
  useEffect(() => {
    const interval = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % bannerMessages.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const nextBanner = () => {
    setBannerIndex((prev) => (prev + 1) % bannerMessages.length)
  }

  const prevBanner = () => {
    setBannerIndex((prev) => (prev - 1 + bannerMessages.length) % bannerMessages.length)
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] font-['Pretendard',sans-serif]">
      {/* Top Banner */}
      <div className="bg-[#FFB800] py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center justify-center flex-1 gap-4">
            <button
              onClick={prevBanner}
              className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center hover:bg-white/50 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <div className="text-center">
              <span className="text-gray-800 text-sm md:text-base">
                {bannerMessages[bannerIndex].text}{' '}
                <strong className="font-bold">{bannerMessages[bannerIndex].highlight}</strong>
              </span>
            </div>
            <button
              onClick={nextBanner}
              className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center hover:bg-white/50 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
          </div>
          <div className="hidden md:flex items-center gap-2">
            {!isAuthenticated && (
              <>
                <Link
                  to="/register"
                  className="px-4 py-1.5 bg-[#FF6B00] text-white rounded-full text-sm font-medium hover:bg-[#e55f00] transition-colors"
                >
                  간편가입
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-1.5 bg-white text-gray-800 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-300"
                >
                  로그인
                </Link>
              </>
            )}
            {isAuthenticated && (
              <Link
                to="/"
                className="px-4 py-1.5 bg-[#FF6B00] text-white rounded-full text-sm font-medium hover:bg-[#e55f00] transition-colors"
              >
                학습하기
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <header className="bg-white shadow-xs sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/landing" className="flex items-center">
              <img src={QuebonLogo} alt="깨봉" className="h-10" />
            </Link>

            {/* Navigation Menu */}
            <nav className="hidden lg:flex items-center gap-8">
              <a href="#" className="text-gray-700 hover:text-[#00A3FF] font-medium transition-colors">
                깨봉소개
              </a>
              <Link to="/products" className="text-gray-700 hover:text-[#00A3FF] font-medium transition-colors">
                학습상품
              </Link>
              <a href="#trial" className="text-gray-700 hover:text-[#00A3FF] font-medium transition-colors">
                무료체험
              </a>
              <a href="#reviews" className="text-gray-700 hover:text-[#00A3FF] font-medium transition-colors">
                학습후기
              </a>
              <a href="#" className="text-gray-700 hover:text-[#00A3FF] font-medium transition-colors">
                고객센터
              </a>
              <a href="#" className="text-gray-700 hover:text-[#00A3FF] font-medium transition-colors">
                이벤트/소식
              </a>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-2">
              {!isAuthenticated && (
                <>
                  <Link
                    to="/register"
                    className="hidden md:inline-flex px-4 py-2 bg-[#FF6B00] text-white rounded-full text-sm font-medium hover:bg-[#e55f00] transition-colors"
                  >
                    간편가입
                  </Link>
                  <Link
                    to="/login"
                    className="px-4 py-2 bg-white text-gray-800 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-300"
                  >
                    로그인
                  </Link>
                </>
              )}
              {isAuthenticated && (
                <Link
                  to="/"
                  className="px-4 py-2 bg-[#00A3FF] text-white rounded-full text-sm font-medium hover:bg-[#0090e0] transition-colors"
                >
                  학습하기
                </Link>
              )}
              {/* Mobile Menu Button */}
              <button className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4 bg-linear-to-br from-[#f0f7ff] to-[#e8f4fc]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Hero Image/Carousel */}
            <div className="w-full lg:w-1/2 order-2 lg:order-1">
              <div className="relative">
                <div className="bg-[#1a1a2e] rounded-2xl overflow-hidden shadow-2xl">
                  <div className="aspect-4/3 flex items-center justify-center p-8">
                    {/* Math Background Pattern */}
                    <div className="absolute inset-0 opacity-30 text-[#3a3a5a] text-xs leading-loose p-4 overflow-hidden">
                      {[...Array(10)].map((_, row) => (
                        <div key={row} className="whitespace-nowrap">
                          {[...Array(8)].map((_, col) => (
                            <span key={col} className="mr-4">
                              {(row + 3) % 9 + 1}×{(col + 1) % 9 + 1}={(((row + 3) % 9 + 1) * ((col + 1) % 9 + 1))}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>

                    {/* Warning Badge */}
                    <div className="absolute top-4 left-4 bg-[#2d2d44] rounded-full px-3 py-1 flex items-center gap-1">
                      <span className="text-red-500 text-lg">⛔</span>
                      <span className="text-white text-xs">구구단 암기는 수포자의 첫걸음!</span>
                    </div>

                    {/* Main Content */}
                    <div className="relative z-10 text-center">
                      <h2 className="text-3xl md:text-4xl font-black text-white mb-2">구구단 절-대</h2>
                      <h2 className="text-4xl md:text-5xl font-black text-[#FFB800]">외우지 마라!</h2>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="bg-white p-4 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-gray-800">깨봉수학</span>
                      <span className="text-gray-400 text-sm">Just now</span>
                    </div>
                    <p className="text-gray-700 text-sm mb-3">
                      구구단 암기는 아이를 망치는 지름길!<br />
                      그럼 어떻게 곱셈을 꿰뚫어야 할까요?
                    </p>
                    <button className="w-full py-2.5 bg-[#fff3e0] text-[#FF6B00] rounded-lg font-medium hover:bg-[#ffe8cc] transition-colors">
                      깨구단으로 끝내기!
                    </button>
                  </div>
                </div>

                {/* Carousel Navigation */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-4 -right-4 flex justify-between pointer-events-none">
                  <button className="pointer-events-auto w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                  </button>
                  <button className="pointer-events-auto w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <ChevronRight className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Hero Text */}
            <div className="w-full lg:w-1/2 text-center lg:text-left order-1 lg:order-2">
              <p className="text-xl md:text-2xl text-gray-700 mb-2">언어처럼 쉽게! 이미지로 재밌게!</p>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                으로 깨우는 <span className="text-[#00A3FF]">진짜 수학머리!</span>
              </h1>
              <p className="text-gray-600 text-base md:text-lg mb-8 max-w-xl mx-auto lg:mx-0">
                공식 암기와 요령으로 망가지는 수학은 이제 그만.<br />
                깨봉으로 AI 시대에 필요한 진짜 문제 해결력을 기르세요.
              </p>
              <Link
                to="/register"
                id="trial"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00A3FF] text-white rounded-full text-lg font-semibold hover:bg-[#0090e0] transition-all hover:scale-105 shadow-lg shadow-blue-500/30"
              >
                <Play className="w-5 h-5" />
                무료체험 시작하기
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${activeCategory === category.id
                  ? 'bg-[#00A3FF] text-white shadow-lg shadow-blue-500/30'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                <category.icon className="w-5 h-5" />
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Guide Section */}
      <section className="py-16 px-4 bg-linear-to-b from-white to-[#f5f7fa]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-0">
              학습안내
            </h2>
            <Link
              to="/products"
              className="text-[#00A3FF] font-medium hover:underline flex items-center gap-1"
            >
              학습안내 자세히 보기
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Learning Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {learningTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all ${activeTab === tab
                  ? 'bg-[#1a1a2e] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((item) => (
                <div key={item} className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow">
                  <div className="w-16 h-16 bg-linear-to-br from-[#00A3FF] to-[#0077CC] rounded-xl mb-4 flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {activeTab} 콘텐츠 {item}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    깨봉만의 특별한 학습 방법으로 수학의 원리를 깨우쳐 보세요.
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-16 px-4 bg-[#f5f7fa]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-0">
              학습 후기
            </h2>
            <a
              href="#"
              className="text-[#00A3FF] font-medium hover:underline flex items-center gap-1"
            >
              체험후기 더보기
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: '김*민 학부모',
                content: '아이가 수학을 싫어했는데 깨봉으로 공부하면서 자신감이 생겼어요!',
                rating: 5,
              },
              {
                name: '이*준 학부모',
                content: '구구단 암기 없이도 곱셈을 이해하게 되었어요. 정말 신기해요.',
                rating: 5,
              },
              {
                name: '박*서 학부모',
                content: '게임처럼 즐기면서 배우니까 아이가 스스로 공부해요!',
                rating: 5,
              },
            ].map((review, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-xs hover:shadow-md transition-shadow">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-lg">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{review.content}"</p>
                <p className="text-gray-500 text-sm">- {review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-linear-to-br from-[#1a1a2e] to-[#2d2d44]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            <span className="text-[#00A3FF]">깨봉수학</span>을 배우면<br />
            누구나 가능합니다
          </h2>
          <p className="text-gray-300 text-lg mb-8">지금 바로 시작하세요!</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-4 bg-[#00A3FF] text-white rounded-full font-semibold hover:bg-[#0090e0] transition-all hover:scale-105 shadow-lg shadow-blue-500/30"
            >
              무료체험 자세히보기
            </Link>
            <div className="flex gap-3">
              <Link
                to="/products"
                className="px-6 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
              >
                학습상품
              </Link>
              <a
                href="#reviews"
                className="px-6 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
              >
                체험후기
              </a>
              <a
                href="#"
                className="px-6 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
              >
                이벤트/소식
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 px-4 border-t">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div className="flex flex-wrap gap-4 mb-4 md:mb-0">
              <a href="#" className="text-gray-600 hover:text-gray-900 text-sm">회사소개</a>
              <a href="#" className="text-gray-600 hover:text-gray-900 text-sm">인재채용</a>
              <a href="#" className="text-gray-600 hover:text-gray-900 text-sm">이용약관</a>
              <a href="#" className="text-gray-600 hover:text-gray-900 text-sm font-bold">개인정보 처리방침</a>
              <a href="#" className="text-gray-600 hover:text-gray-900 text-sm">사업자정보확인</a>
            </div>
            <div className="flex gap-3">
              <a href="https://youtube.com" target="_blank" rel="noreferrer" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a href="https://blog.naver.com" target="_blank" rel="noreferrer" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                <span className="text-gray-600 font-bold text-xs">N</span>
              </a>
            </div>
          </div>

          <div className="text-gray-500 text-sm space-y-1">
            <p><strong className="text-gray-700">이쿠얼키(주)</strong> <span className="mx-2">|</span> 대표 조봉한</p>
            <p>주소: 서울특별시 강남구 선릉로 108길 51, 2층</p>
            <p>사업자번호: 426-87-00450 <span className="mx-2">|</span> 통신판매업 신고번호: 제 2020-서울강남-00938 호</p>
            <p>학원설립 운영등록번호: 제 13169 호 인공지능수학깨봉원격학원</p>
            <p>신고기관명: 강남서초교육지원청</p>
          </div>

          <div className="mt-8 pt-8 border-t text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Quebon. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-linear-to-r from-[#00A3FF] to-[#0077CC] py-4 px-4 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#00A3FF] font-bold text-[10px]">QUE</span>
              </div>
              <div className="w-8 h-8 bg-[#FFB800] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-[10px]">BON</span>
              </div>
            </div>
            <Link
              to="/register"
              className="text-white font-semibold text-lg hover:underline"
            >
              무료체험 이벤트
            </Link>
          </div>
          <button className="px-6 py-2.5 bg-white text-[#00A3FF] rounded-full font-semibold hover:bg-gray-100 transition-colors">
            전화상담 간편 신청
          </button>
        </div>
      </div>

      {/* Bottom Padding for Fixed Bar */}
      <div className="h-20"></div>
    </div>
  )
}

